import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BezwaarBelastingmiddel,
  BezwaarEntity, BezwaarSoort, BezwaarSpecificatie,
  BokkzAlgDataService, BokkzBezwaarClient, BokkzBezwaarDataService, ContactPersoon
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ToastService} from "@snelstart/ui-toast";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {VoorbeeldTekst, VOORBEELDTEKSTEN} from "../voorbeeldTeksten/voorbeeldTeksten.component";
import { filter, takeUntil } from "rxjs";
import { Event as Event_2, NavigationEnd, Router } from "@angular/router";
import { dispatchSavedItemEvent } from "@snelstart/domain-logic";

@Component({
  selector: 'snelstart-venture-fiscaal-bezwaar',
  standalone: true,
  imports: [CommonModule, SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule],
  templateUrl: './bezwaar.component.html',
  styleUrl: './bezwaar.component.scss',
})
export class BezwaarComponent implements OnChanges {
  @Input() public id!: string;
  public clientId: any;
  private isSaving = false;
  public bezwaarEntity = {} as BezwaarEntity;
  public formGroup = {} as FormGroup;

  voorbeeldTeksten: VoorbeeldTekst[] = VOORBEELDTEKSTEN;

  get gefilterdeVoorbeeldTeksten(): VoorbeeldTekst[] {
    const middel = this.bezwaarEntity?.bezwaarschrift?.bezwaarSpecificatie?.belastingMiddel;
    return this.voorbeeldTeksten.filter(
      tekst => !tekst.belastingMiddelen || (middel && tekst.belastingMiddelen.includes(middel))
    );
  }

  getNaamContactPersoon(contactPersoon: ContactPersoon): string {
      let naam = '';
      if (contactPersoon.voorletters) {
        naam += contactPersoon.voorletters + ' ';
      }
      if (contactPersoon.tussenvoegsel) {
        naam += contactPersoon.tussenvoegsel + ' ';
      }
      if (contactPersoon.achternaam) {
        naam += contactPersoon.achternaam;
      }
      return naam.trim();
    }

  selecteerVoorbeeldtekst(tekst: string): void {
    const contactPersoon = this.bezwaarEntity?.bezwaarschrift?.contactPersoon;
    const naamContactPersoon = contactPersoon ? this.getNaamContactPersoon(contactPersoon) : '';

    const placeholders = {
      naamContactPersoon: naamContactPersoon,
      naamKantoor: this.bezwaarEntity?.bezwaarschrift?.intermediair?.naamDienstverlener ?? '',
      bsnBelanghebbende: this.bezwaarEntity.bezwaarschrift?.bsnBelanghebbende ?? '',
      naamBelanghebbende: this.formatNaam(this.bezwaarEntity.bezwaarschrift?.naamBelanghebbende)?? '',
      jaar: this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie?.aanslagJaar ?? '',
      aanslagnummer: this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie?.aanslagnummer ?? '',
    };

    const ingevuldeTekst = this.vervangPlaceholders(tekst, placeholders);
    const huidig = this.formGroup.get('motivatie')?.value || '';
    const nieuw = huidig ? `${huidig}\n\n${ingevuldeTekst}` : ingevuldeTekst;

    this.formGroup.get('motivatie')?.setValue(nieuw);
    this.formGroup.get('motivatie')?.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  private vervangPlaceholders(tekst: string, waarden: Record<string, string>): string {
    return tekst.replace(/{{(.*?)}}/g, (_, key) => waarden[key.trim()] || '');
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzBezwaarDataService,
    private router: Router,
    public bokkzClient: BokkzBezwaarClient,
    public toastService: ToastService,
    public fb: FormBuilder,
  ) {
    this.formGroup = this.fb.group<BezwaarSpecificatie>({
      dagtekening: null as unknown as Date,
      aanslagnummer: null as unknown as string,
      aanslagJaar: null as unknown as string,
      belastingMiddel: null as unknown as BezwaarBelastingmiddel,
      bezwaarBox3: null as unknown as boolean,
      bezwaarOndernemerschapIh: null as unknown as boolean,
      motivatie: null as unknown as string,
      bezwaarSoort: null as unknown as BezwaarSoort,
      bijlagen: []
    })
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const idChanges = changes['id'];
    if (idChanges && idChanges.currentValue !== idChanges.previousValue) {
      this.algDataService.client.subscribe((client) => {
        this.clientId = client.sstRelationId;})
      this.dataService.bezwaarEntity.subscribe((bezwaarEntity) => {
          this.bezwaarEntity = bezwaarEntity;
        if (bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie) {
            this.formGroup.reset()
            this.formGroup.patchValue(bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie);
          }
        });
    }
  }



  public saveForm() {
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.bezwaarEntity = {
      ...this.bezwaarEntity,
      bezwaarschrift: {
        ...this.bezwaarEntity.bezwaarschrift,
        bezwaarSpecificatie: {
          ...this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie,
          motivatie: this.formGroup.getRawValue().motivatie,
          bijlagen: this.formGroup.getRawValue().bijlagen
        }
      }
    }
    if (this.formGroup.dirty && !this.isSaving) {
        this.isSaving = true;
        this.bokkzClient.postOneBezwaar(this.clientId,false, this.bezwaarEntity).subscribe((bezwaarEntity)=> {
          this.dataService.updateItem(bezwaarEntity);
          dispatchSavedItemEvent({
            identifier: Number(bezwaarEntity.id),
            result: bezwaarEntity
          });
        });
        this.isSaving = false;
    }
  }

  public formatNaam(naam: string | undefined): string {
    if (!naam?.includes(',')) return naam as string;
    const [achternaam, rest] = naam.split(',').map(part => part.trim());
    return `${rest} ${achternaam}`.trim();
  }

}
